import api from '../services/api';

export type PrinterProtocol = 'ESCPOS' | 'ZPL';

type PrinterApiItem = {
    id: number;
    nome: string;
    ip: string;
    porta: number;
    protocolo?: string | null;
    ativo?: boolean;
};

type StoredPrinterConfig = {
    id?: number;
    nome?: string;
    ip: string;
    porta: number;
    protocolo?: string;
};

export type ResolvedPrinterConfig = {
    id: number;
    nome: string;
    ip: string;
    porta: number;
    protocolo: PrinterProtocol;
};

const STORAGE_KEY = 'impressora_config';

function normalizeProtocol(value?: string | null): PrinterProtocol | null {
    const normalized = String(value || '').trim().toUpperCase();
    if (normalized === 'ESCPOS') return 'ESCPOS';
    if (normalized === 'ZPL') return 'ZPL';
    return null;
}

function parseStoredConfig(): StoredPrinterConfig | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw) as Partial<StoredPrinterConfig>;
        if (!parsed.ip || !parsed.porta) return null;

        return {
            id: parsed.id,
            nome: parsed.nome,
            ip: parsed.ip,
            porta: Number(parsed.porta),
            protocolo: parsed.protocolo,
        };
    } catch {
        return null;
    }
}

function toResolvedPrinter(printer: PrinterApiItem): ResolvedPrinterConfig | null {
    const protocolo = normalizeProtocol(printer.protocolo);
    if (!protocolo) return null;

    return {
        id: printer.id,
        nome: printer.nome,
        ip: printer.ip,
        porta: Number(printer.porta),
        protocolo,
    };
}

export function savePrinterConfig(printer: {
    id?: number;
    nome?: string;
    ip: string;
    porta: number;
    protocolo?: string | null;
}) {
    localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
            id: printer.id,
            nome: printer.nome,
            ip: printer.ip,
            porta: printer.porta,
            protocolo: normalizeProtocol(printer.protocolo || undefined),
        })
    );
}

export async function resolvePrinterForProtocol(protocol: PrinterProtocol): Promise<ResolvedPrinterConfig | null> {
    const response = await api.get('/auth/impressoras');
    const rawList = Array.isArray(response.data?.data) ? response.data.data : [];

    const activeOrAll = rawList.some((item: PrinterApiItem) => item.ativo)
        ? rawList.filter((item: PrinterApiItem) => item.ativo)
        : rawList;

    const printers = activeOrAll
        .map((item: PrinterApiItem) => toResolvedPrinter(item))
        .filter(Boolean) as ResolvedPrinterConfig[];

    if (printers.length === 0) {
        return null;
    }

    const current = parseStoredConfig();

    const currentPrinter = current
        ? printers.find((printer) => {
            if (current.id && printer.id === current.id) return true;
            return printer.ip === current.ip && Number(printer.porta) === Number(current.porta);
        })
        : null;

    const sameProtocol = currentPrinter?.protocolo === protocol ? currentPrinter : null;
    const firstByProtocol = printers.find((printer) => printer.protocolo === protocol) || null;
    const chosen = sameProtocol || firstByProtocol || currentPrinter || printers[0];

    savePrinterConfig(chosen);
    return chosen;
}